// src/hooks/useBrands.js
import { useState, useCallback, useRef, useEffect } from "react";
import { getBrandNamesList } from "../actions/BrandAction";

// Module-level shared state
let sharedBrands = [];
let currentPage = 1;
let totalPages = null;
let isFetching = false;
let cachedCategory = undefined;
const listeners = new Set();

function notifyListeners() {
  listeners.forEach((fn) => fn([...sharedBrands]));
}

// Call this to wipe shared state (e.g. on logout)
export function resetBrandCache() {
  sharedBrands = [];
  currentPage = 1;
  totalPages = null;
  isFetching = false;
  cachedCategory = undefined;
}

export function useBrands(category = "") {
  const [brands, setBrands] = useState([...sharedBrands]);
  const [loading, setLoading] = useState(false);

  // hasMore: true if we haven't fetched yet OR there are more pages
  const hasMore = totalPages === null || currentPage <= totalPages;

  const listenerRef = useRef(null);
  if (!listenerRef.current) {
    listenerRef.current = (updated) => setBrands(updated);
    listeners.add(listenerRef.current);
  }

  const cleanup = useCallback(() => {
    listeners.delete(listenerRef.current);
  }, []);

  const loadMore = useCallback(async () => {
    const stillHasMore = totalPages === null || currentPage <= totalPages;
    if (isFetching || !stillHasMore) return;

    isFetching = true;
    setLoading(true);

    try {
      const response = await getBrandNamesList(currentPage, 10, category);
      const newBrands = response.brands || [];
      totalPages = response.totalPages ?? 1;

      // Avoid duplicates by checking IDs
      const existingIds = new Set(sharedBrands.map((b) => b.id));
      const uniqueNew = newBrands.filter((b) => !existingIds.has(b.id));

      sharedBrands = [...sharedBrands, ...uniqueNew];
      currentPage += 1;
      setBrands([...sharedBrands]); 
      notifyListeners();
    } catch (err) {
      console.error("Failed to load brands:", err);
    } finally {
      isFetching = false;
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    if (cachedCategory !== category) {
      sharedBrands = [];
      currentPage = 1;
      totalPages = null;
      isFetching = false;
      cachedCategory = category;
      setBrands([]);
      loadMore();
    } else if (sharedBrands.length === 0) {
      loadMore();
    } else {
      setBrands([...sharedBrands]);
    }

    return cleanup;
  }, [category, loadMore, cleanup]);

  return { brands, loading, hasMore, loadMore, cleanup };
}
